/**
 * Cloned from Shell App https://github.com/MGMResorts/web-mfe-shell/blob/main/src/common/context/FlagsContext.tsx
 */
import { Environment, fetchUserKey, FlagsSdk } from '@mgmresorts/client-feature-flagging';
import React, { useState, useEffect, ReactNode, useMemo, useCallback } from 'react';

import { useDerivedPropertySlug } from '../../hooks/useDerivedPropertySlug';
import { useAuthContext } from '../../utils/auth/AuthProvider';
import { ClientIds } from '../../utils/constants';
import { actually } from '../../utils/logging';
import { getPersistedParameters } from '../../utils/persistParameterContext';

import { useFeatureFlagsValue } from './FlagsValueContext';
import defaultFlags from './defaultFlags.json';

export type Flags = Awaited<ReturnType<(typeof FlagsSdk)['fetch']>>;
export type UserKey = Awaited<ReturnType<typeof fetchUserKey>> | undefined;

interface FlagsProviderProps {
  children: ReactNode;
}

interface FlagsData {
  flags: Flags;
  isFulfilled: boolean;
}
interface FlagType {
  __typename?: 'FeatureFlagVariationDetail';
  flagKey: string;
  flagValue?: string;
  userKey: string;
}

export interface FlagsContextT {
  flags: Flags;
  getFlagByKey: (key: string) => NonNullable<FlagType>['flagValue'];
  userKey: UserKey;
}

export const FlagsContext = React.createContext<FlagsContextT>({
  flags: [],
  getFlagByKey: () => undefined,
  userKey: undefined,
});

export const FlagsProvider = ({ children }: FlagsProviderProps) => {
  const { acquireTransientToken } = useAuthContext();
  const { setFlags } = useFeatureFlagsValue();

  const [flagsData, setFlagsData] = useState<FlagsData>({ flags: [], isFulfilled: false });
  const [userKey, setUserKey] = useState<UserKey>('');
  const [initializedSDK, setInitializedSDK] = useState(false);
  const property = useDerivedPropertySlug();
  const params = getPersistedParameters();
  const clientId = params?.client_id ?? '';
  const isMgmWebApp = clientId === ClientIds.MGM_APP_WEB;

  // Handle userKey fetching.
  useEffect(() => {
    if (process.env.IN_JEST === 'true') {
      setFlags([]);
      return;
    }

    const fetchUserKeyAsync = async () => {
      try {
        const userKey = await fetchUserKey();
        setUserKey(userKey);
      } catch (err) {
        actually.error(err);
        setFlags([]);
      }
    };

    fetchUserKeyAsync().catch(e => actually.error(e));
  }, [setFlags]);

  // Handle SDK initialization.
  useEffect(() => {
    if (userKey) {
      const environment = process.env.REACT_APP_ENVIRONMENT?.toUpperCase() as Environment;
      const name = 'mgm-identity-oidc-app';

      const fetchToken = async () => {
        const authToken = await acquireTransientToken();
        const params = getPersistedParameters();

        if (authToken) {
          FlagsSdk.init({
            authToken: authToken?.accessToken,
            environment: environment === 'DEV' ? 'UAT' : environment ?? 'PROD',
            name,
            userKey,
            attributes: {
              // Used for feature flag targeting in LaunchDarkly, but can also be used for debugging purposes
              customAttributes: {
                path: window?.location?.pathname ?? '',
                clientId: params?.client_id ?? '',
                propertyName: property ?? '',
              },
            },
          });
          setInitializedSDK(true);
        }
      };

      fetchToken();
    }
  }, [userKey, acquireTransientToken, property]);

  // Handle flag fetching when auth and userKey are ready.
  useEffect(() => {
    const fetchFlagsAsync = async () => {
      try {
        if (initializedSDK) {
          const fetchedFlags = await FlagsSdk.fetch();
          if (fetchedFlags) {
            setFlagsData({ flags: fetchedFlags, isFulfilled: true });
            setFlags(fetchedFlags);
          } else {
            const fallbackFlags = isMgmWebApp ? defaultFlags : [];
            setFlagsData({ flags: fallbackFlags as Flags, isFulfilled: true });
            setFlags(fallbackFlags as Flags);
          }
        }
      } catch (err) {
        actually.error(err);
        const fallbackFlags = isMgmWebApp ? defaultFlags : [];
        setFlagsData({ flags: fallbackFlags as Flags, isFulfilled: true });
        setFlags(fallbackFlags as Flags);
      }
    };

    fetchFlagsAsync().catch(e => actually.error(e));
  }, [initializedSDK, setFlags, isMgmWebApp]);

  const getFlagByKey: FlagsContextT['getFlagByKey'] = useCallback(
    (key: string) => flagsData.flags?.find(x => x?.flagKey === key)?.flagValue,
    [flagsData.flags],
  );

  const value: FlagsContextT = useMemo(() => {
    return {
      flags: flagsData.flags,
      getFlagByKey,
      userKey,
    };
  }, [flagsData, getFlagByKey, userKey]);

  return <FlagsContext.Provider value={value}>{children}</FlagsContext.Provider>;
};

export const useFeatureFlags = () => {
  const context = React.useContext(FlagsContext);

  if (context === undefined) {
    throw new Error('useFlags must be used within a FlagsProvider');
  }

  return context;
};

export enum FEATURE_FLAGS {
  OKTA_IDX_API = 'IDENT-9479_enable-okta-idx-api_2026-01-21_temporary',
  ENABLE_MFA = 'IDENT-9543_enable-mfa_2026-01-28_temporary',
  ENABLE_PASSKEY = 'IDENT-10035_enable-passkey_2026-03-23_temporary',
  ENABLE_POST_SIGNIN_2FA_ENROLLMENT = 'PROSERV-2431',
  PREVENT_AUTO_SIGN_IN = 'PROSERV-2250',
  REMOVE_2FA_SKIP_FOR_NOW = 'PROSERV-2513',
}
